import { useCallback } from 'react';
import {
  SegmentBoundaryTransition,
  SegmentTransitionType,
  createSegmentTransition
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/SegmentTransition';
import { Segment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { updateClipData } from '../clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '../clipUpdateDataUtils/constants';
import { getSegmentTransitionBoundaryIds } from '../clipUpdateDataUtilsWithSplits/segmentTransitionUtils';
import { useAppContext } from '@/context/AppContext/AppContext';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';

interface UseSegmentTransitionParams {
  previousSegment: Segment | null | undefined;
  nextSegment: Segment | null | undefined;
  isTransitionApplied: boolean;
}

export function useSegmentTransition({
  previousSegment,
  nextSegment,
  isTransitionApplied
}: UseSegmentTransitionParams) {
  const { analytics } = useAppContext();
  const { clipId, fps } = useClipsContext();

  const canToggle = !!getSegmentTransitionBoundaryIds(previousSegment ?? undefined, nextSegment ?? undefined);

  const toggleTransition = useCallback(
    (onComplete?: () => void) => {
      const boundaryIds = getSegmentTransitionBoundaryIds(previousSegment ?? undefined, nextSegment ?? undefined);
      if (!boundaryIds) {
        return;
      }
      if (isTransitionApplied) {
        updateClipData({
          clipId,
          actionType: CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRANSITION_OUT,
          ...boundaryIds
        });
        analytics.track('ContentLabEvent', {
          name: 'SegmentTransitionRemoved',
          clipId,
          ...boundaryIds
        });
      } else {
        const transition: SegmentBoundaryTransition = createSegmentTransition({
          transitionType: SegmentTransitionType.FADE,
          ...boundaryIds,
          fps
        });
        updateClipData({
          clipId,
          actionType: CLIP_UPDATE_TYPE.UPSERT_SEGMENT_TRANSITION_OUT,
          transition
        });
        analytics.track('ContentLabEvent', {
          name: 'SegmentTransitionAdded',
          clipId,
          ...boundaryIds
        });
      }
      onComplete?.();
    },
    [previousSegment, nextSegment, isTransitionApplied, clipId, fps, analytics]
  );

  return { toggleTransition, canToggle };
}
